import Close from '@mui/icons-material/Close'
import { DialogContent, DialogTitle } from '@mui/material'
import { genericHandleChange } from 'src/consumer/helpers/utilFunctions'
import PillInputComponent from '../common/PillInputComponent'
import { getDrugItemOptionLabel, getDrugItemOptionValue } from './utils'
import { DocOnContextSectionContainer, DocOnContextSectionHeader } from '../common/styledComponents'

// Each medicine attribute gets its own distinctly-coloured card so they read as separate,
// scannable groups in the (touch-first) contextual drawer.
const CATEGORY_THEME = {
  dosage: { card: '#eff5ff', border: '#bfdbfe', header: '#2563eb', headerText: '#ffffff', pill: '#dbeafe' },
  whatTime: { card: '#ecfdf5', border: '#a7f3d0', header: '#059669', headerText: '#ffffff', pill: '#d1fae5' },
  frequency: { card: '#f5f3ff', border: '#ddd6fe', header: '#7c3aed', headerText: '#ffffff', pill: '#ede9fe' },
  duration: { card: '#fff7ed', border: '#fed7aa', header: '#ea580c', headerText: '#ffffff', pill: '#ffedd5' },
  notes: { card: '#fdf2f8', border: '#fbcfe8', header: '#db2777', headerText: '#ffffff', pill: '#fce7f3' },
}

export default function DrugAdviceContextForm({
  item: drug,
  setItem: setDrug,
  saveItem,
  closeMenu,
  durationOptions,
  whatTimeOptions,
  frequencyOptions,
  dosageOptions,
  notesOptions,
  prescriptionViewSettings,
}) {
  function handleDrugChange(e) {
    genericHandleChange(e, setDrug)
  }

  // Order matters — Dosage → What time → Frequency → Duration → Notes (top to bottom).
  const categories = [
    { key: 'dosage', title: 'Dosage', valueKey: 'dosage', value: drug?.dosage, options: dosageOptions, placeholder: 'Search dosage or type…' },
    { key: 'whatTime', title: 'What time', valueKey: 'whatTime', value: drug?.whatTime, options: whatTimeOptions, placeholder: 'Search time or type…' },
    { key: 'frequency', title: 'Frequency', valueKey: 'frequency', value: drug?.frequency, options: frequencyOptions, placeholder: 'Search frequency or type…' },
    { key: 'duration', title: 'Duration', valueKey: 'duration', value: drug?.duration, options: durationOptions, placeholder: 'Search duration or type…' },
    { key: 'notes', title: 'Notes', valueKey: 'notes', value: drug?.notes, options: notesOptions, placeholder: 'Search notes or type…' },
  ]

  return (
    <>
      <DialogTitle
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: '#f4f5f9',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '10px',
          alignItems: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <div
          style={{
            fontWeight: 700,
            fontSize: '16px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {drug?.label}
        </div>
        <button
          className="d-flex flex-column align-items-center justify-content-center ms-2"
          onClick={() => closeMenu()}
        >
          <Close />
          <span className="fs-7 fw-normal">Close</span>
        </button>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '12px', py: 2 }}>
        {categories.map((cat) => {
          const t = CATEGORY_THEME[cat.key]
          return (
            <DocOnContextSectionContainer key={cat.key} cardBg={t.card} borderColor={t.border}>
              <DocOnContextSectionHeader headerBg={t.header} headerColor={t.headerText}>
                {cat.title}
              </DocOnContextSectionHeader>
              <div className="p-2">
                <PillInputComponent
                  value={cat.value}
                  handleChange={handleDrugChange}
                  valueKey={cat.valueKey}
                  searchable
                  searchPlaceholder={cat.placeholder}
                  maxPillRows={2}
                  pillUnselectedBackground={t.pill}
                  options={removeItemsWithDuplicateLabels({
                    options: cat.options || [],
                    getLabel: (option) => getDrugItemOptionLabel(option, prescriptionViewSettings),
                  })}
                  getOptionValue={(option) => getDrugItemOptionValue(option, prescriptionViewSettings)}
                  getOptionLabel={(option) => getDrugItemOptionLabel(option, prescriptionViewSettings)}
                />
              </div>
            </DocOnContextSectionContainer>
          )
        })}
      </DialogContent>
    </>
  )
}

function removeItemsWithDuplicateLabels({ options, getLabel }) {
  const labels = new Set()
  return options.filter((option) => {
    const label = getLabel(option)
    if (labels.has(label)) {
      return false
    }
    labels.add(label)
    return true
  })
}
